import SlideFrame from "@/components/SlideFrame";
import { Quote } from "lucide-react";
import { packFor } from "@/lib/industryPack";

/* ============================ 02 · The Problem ============================ */
export function S_Problem({ idx, total, prospect }) {
  const pack = packFor(prospect);
  const corp = pack.corporate;
  const lf = pack.lawFirm;
  const showCustomHeadline = corp || lf;
  const isMortgage = /mortgage/i.test(prospect?.industry || "");
  // Override one card with mortgage-specific concerns for lenders like Sage.
  const cards = isMortgage
    ? pack.problemCards.map((c, i) =>
        i === 0
          ? {
              title: "Mortgage compliance never sleeps",
              body: "RESPA, TRID, HMDA, fair-lending, plus a state-by-state licensing matrix — every new state is another audit posture, another exam cycle, another set of disclosures to police across your LO marketing.",
            }
          : c
      )
    : pack.problemCards;
  return (
    <SlideFrame idx={idx} total={total} kicker="The Problem" dark>
      <div className="grid grid-cols-12 gap-10 pt-4 md:pt-6">
        <div className="col-span-12 md:col-span-7">
          <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-5">
            / what you're already feeling{prospect ? ` — ${pack.label}` : ""}
          </p>
          {showCustomHeadline ? (
            <h2 className="serif font-light text-5xl md:text-6xl leading-[1.04] tracking-tight">
              {pack.problemHeadline.lead}<br />
              <em className="italic text-sun-600">{pack.problemHeadline.italic}</em><br />
              {pack.problemHeadline.trail}
            </h2>
          ) : (
            <h2 className="serif font-light text-5xl md:text-6xl leading-[1.04] tracking-tight">
              Compliance keeps growing.<br />
              <em className="italic text-sun-600">Your team isn't.</em>
            </h2>
          )}
          {showCustomHeadline ? (
            <>
              <p className="mt-6 text-ink-900/80 text-lg max-w-2xl leading-relaxed">
                {pack.problemBody1}
              </p>
              {pack.problemBody2 && (
                <p className="mt-3 text-ink-900/80 text-lg max-w-2xl leading-relaxed">
                  {pack.problemBody2}
                </p>
              )}
            </>
          ) : (
            <p className="mt-6 text-ink-900/80 text-lg max-w-2xl leading-relaxed">
              Regulators are getting personal. Partners want a sharper packet every quarter.
              Your team is already using AI — usually without guardrails. Every line, every
              campaign, every new state is another fire to put out.
            </p>
          )}

          {!pack.suppressProblemQuote && (
            <div className="mt-7 border-l-4 border-sun-500 pl-5 max-w-2xl" data-testid="problem-pia-quote">
              <Quote className="h-5 w-5 text-sun-700 mb-2" strokeWidth={1.5} />
              <p className="serif text-lg md:text-xl leading-snug text-ink-900 italic">
                {corp
                  ? `"We chose the right partner with CompliSun. The work gets done, no surprises, and the quality is better than most full-time hires we've considered."`
                  : `"We chose the right partner with CompliSun. I honestly don't see bringing in a whole Compliance Department again — the work gets done, no sacrifices, and far better quality than most full-time hires."`}
              </p>
              <div className="mt-3 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">
                Pia Thompson · CEO, Checkbook
              </div>
            </div>
          )}
        </div>

        <div className="col-span-12 md:col-span-5 stagger flex flex-col gap-3">
          {cards.map((c, i) => (
            <div key={c.title}
                 data-testid={`problem-card-${i}`}
                 className="border border-ink-900/10 rounded-md p-4 bg-white/70 hover:bg-white transition-colors">
              <div className="serif text-base md:text-lg text-ink-900 leading-snug">{c.title}</div>
              <div className="text-sm text-ink-900/65 mt-1.5 leading-relaxed">{c.body}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideFrame>
  );
}
